/**
 * Student Query Functions
 *
 * Student absence details and search functionality.
 * @module fehlzeiten/services/queries/studentQueries
 */

import { supabase } from "../../../../../lib/supabase";

export interface StudentAbsenceDetailRow {
  id: string;
  date: string;
  date_display?: string;
  date_iso?: string;
  status: string;
  absence_status?: string;
  reason: string | null;
  is_full_day: boolean;
  is_excused?: boolean;
  start_time: string | null;
  end_time: string | null;
  time_range?: string;
  period: number | null;
  period_label?: string;
  subject: string | null;
  subject_name?: string;
  approval_action: string | null;
  approved_at?: string | null;
  lesson_id: string | null;
  note_id?: string | null;
  reviewed_by_name?: string | null;
  reviewed_at?: string | null;
  record_type: string;
  class_at_lesson_id?: string | null;
  class_at_lesson_name?: string | null;
  has_status_conflict?: boolean; // True if note says excused but logs have unexcused entries
  exclude_from_zeugnis?: boolean; // True if absence is excluded from report card
}

export interface StudentSearchResult {
  id: string;
  name: string;
  class_id?: string;
  class_name?: string;
}

/**
 * Fetches detailed absence entries for a student within a date range.
 */
export async function fetchStudentAbsenceDetails(
  studentId: string,
  startDate: string,
  endDate: string,
  detailType: string = "days",
  includeFullDay: boolean = true,
): Promise<StudentAbsenceDetailRow[]> {
  const { data, error } = await supabase.rpc(
    "fn_get_student_absence_details" as never,
    {
      p_student_id: studentId,
      p_start_date: startDate,
      p_end_date: endDate,
      p_detail_type: detailType,
      p_include_full_day_lessons: includeFullDay,
    } as never,
  );

  if (error) {
    console.error("[fetchStudentAbsenceDetails] Error:", error);
    return [];
  }

  return (data as unknown as StudentAbsenceDetailRow[]) || [];
}

/**
 * Searches for students by name within a school.
 * Uses the fn_search_students RPC for a single server-side query
 * that joins user_profiles + mv_student_current_class + structure_classes.
 */
export async function searchStudents(
  query: string,
  schoolId: string,
): Promise<StudentSearchResult[]> {
  if (!query || query.trim().length < 2 || !schoolId) {
    return [];
  }

  try {
    const { data, error } = await supabase.rpc(
      "fn_search_students" as never,
      {
        p_query: query.trim(),
        p_limit: 10,
      } as never,
    );

    if (error) {
      console.error("[searchStudents] RPC error:", error);
      return [];
    }

    return ((data as any[]) || []).map((r: any) => ({
      id: r.id,
      name: r.full_name,
      class_id: r.class_id || undefined,
      class_name: r.class_name || undefined,
    }));
  } catch (err) {
    console.error("[searchStudents] Unexpected error:", err);
    return [];
  }
}
